import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
    GetPlansResponse,
    GetPlanStatusResponse,
    CheckDeckLimitResponse,
    CheckCreditResponse,
    PurchaseOrderResponse,
    DeckUnlockRequest,
    CreditPurchaseRequest,
    DeckType,
} from '@/types/pricing.types';

export const pricingApi = createApi({
    reducerPath: 'pricingApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.NEXT_PUBLIC_API_URL}/pricing`,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth.token ||
                (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['PlanStatus'],
    endpoints: (builder) => ({
        // ─── Public ────────────────────────────────────────────
        getPlans: builder.query<GetPlansResponse, string | void>({
            query: (currency) => ({
                url: '/plans',
                params: currency ? { currency } : undefined,
            }),
        }),

        // ─── Protected Queries ─────────────────────────────────
        getPlanStatus: builder.query<GetPlanStatusResponse, string | void>({
            query: (currency) => ({
                url: '/status',
                params: currency ? { currency } : undefined,
            }),
            providesTags: ['PlanStatus'],
        }),

        checkDeckLimit: builder.query<CheckDeckLimitResponse, { deckType: DeckType; currency?: string }>({
            query: ({ deckType, currency }) => ({
                url: '/check-limit',
                params: {
                    deckType,
                    ...(currency && { currency }),
                },
            }),
        }),

        checkDocumentCredit: builder.query<CheckCreditResponse, void>({
            query: () => '/check-document-credit',
        }),

        checkMarketResearchCredit: builder.query<CheckCreditResponse, void>({
            query: () => '/check-market-research-credit',
        }),

        // ─── Purchase Mutations ────────────────────────────────
        purchaseGrowthPack: builder.mutation<PurchaseOrderResponse, void>({
            query: () => ({
                url: '/purchase/growth-pack',
                method: 'POST',
            }),
            invalidatesTags: ['PlanStatus'],
        }),

        purchaseDeckUnlock: builder.mutation<PurchaseOrderResponse, DeckUnlockRequest>({
            query: (body) => ({
                url: '/purchase/deck-unlock',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PlanStatus'],
        }),

        purchaseFundraisingAddon: builder.mutation<PurchaseOrderResponse, void>({
            query: () => ({
                url: '/purchase/fundraising-addon',
                method: 'POST',
            }),
            invalidatesTags: ['PlanStatus'],
        }),

        purchaseDocumentCredits: builder.mutation<PurchaseOrderResponse, CreditPurchaseRequest & { currency?: string }>({
            query: (body) => ({
                url: '/purchase/document',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PlanStatus'],
        }),

        purchaseMarketResearchCredits: builder.mutation<PurchaseOrderResponse, CreditPurchaseRequest & { currency?: string }>({
            query: (body) => ({
                url: '/purchase/market-research',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PlanStatus'],
        }),
    }),
});

export const {
    useGetPlansQuery,
    useGetPlanStatusQuery,
    useCheckDeckLimitQuery,
    useLazyCheckDeckLimitQuery,
    useCheckDocumentCreditQuery,
    useLazyCheckDocumentCreditQuery,
    useCheckMarketResearchCreditQuery,
    useLazyCheckMarketResearchCreditQuery,
    usePurchaseGrowthPackMutation,
    usePurchaseDeckUnlockMutation,
    usePurchaseFundraisingAddonMutation,
    usePurchaseDocumentCreditsMutation,
    usePurchaseMarketResearchCreditsMutation,
} = pricingApi;
